import { useState, useCallback, useEffect, useRef } from 'react';
import { Scenario, ScenarioType } from '../types/app';
import { CalculatorParams } from '../types/api';
import { generateId, sanitizeObject } from '../utils/formatters';
import { MAX_FREE_SCENARIOS, DEFAULT_TIME_HORIZON } from '../utils/constants';
import { dlog, debugEnabled } from '../utils/debug';

// Fallback defaults if API call fails or hasn't completed
const FALLBACK_DEFAULTS: CalculatorParams = {
  property_details: {
    home_price: 800000,
    property_tax_rate: 0.0125,
    hoa_monthly: 0.0,
    insurance_monthly: 200.0,
    maintenance_rate: 0.01,
  },
  loan_details: {
    loan_type: 'fha' as any,
    mortgage_interest_rate: 0.0625,
    loan_term_years: 30,
    down_payment_percent: 0.05,
    pmi_rate: 0.005,
    pmi_removal_threshold: 0.80,
    ufmip_rate: 0.0175,
    finance_ufmip: true,
    pmi_removal_strategy: 'scheduled' as any,
    appraisal_year: null,
    appraisal_ltv_override: null,
    fha_mip_rate: 0.0085,
    fha_mip_removal_years: 11,
    fha_min_down_payment_percent: 0.035,
    fha_high_down_payment_threshold: 0.10,
    conventional_pmi_threshold: 0.20,
    fha_loan_limit_base: 625000.0,
    fha_loan_limit_high_cost: 1209750.0,
  },
  transaction_costs: {
    realtor_fees_percent: 0.03,
    closing_costs: 4000.0,
    selling_realtor_fees_percent: 0.055,
    selling_closing_costs: 3000.0,
  },
  market_assumptions: {
    property_appreciation_rate: 0.04,
    investment_return_rate: 0.07,
    rent_increase_rate: 0.03,
  },
  analysis_settings: {
    time_horizon_years: 16,
  },
  renting_params: {
    current_rent_with_roommates: 0.0,
    current_rent_living_alone: 3500.0,
    move_out_year: 0,
    rental_strategy: {
      monthly_income: 0.0,
      occupancy_rate: 1.0,
      duration_years: 16,
    },
  },
  buying_params: {
    rental_strategy: {
      monthly_income: 0.0,
      occupancy_rate: 1.0,
      duration_years: 16,
    },
  },
};

const STORAGE_KEY = 'rent-vs-buy-scenarios';
const STORAGE_VERSION = '2.0'; // Updated version for new structure

interface StoredState {
  version: string;
  scenarios: Scenario[];
  activeScenarioId: string | null;
  timeHorizonYears: number;
  // Removed viewMode - each scenario now has its own type
}

export const useScenarios = (
  defaultParams?: CalculatorParams,
  maxScenarios: number = MAX_FREE_SCENARIOS
) => {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [activeScenarioId, setActiveScenarioId] = useState<string | null>(null);
  const [timeHorizonYears, setTimeHorizonYears] = useState(DEFAULT_TIME_HORIZON);
  // Removed viewMode state - each scenario now has its own type
  const defaultsAppliedRef = useRef(false);

  // Phase 3.1: Track scenarios array stability
  const scenariosRef = useRef(scenarios);
  useEffect(() => {
    if (scenariosRef.current !== scenarios) {
      console.log('[SCENARIOS_CHANGED]', {
        oldLength: scenariosRef.current.length,
        newLength: scenarios.length,
        changed: scenarios.map((s, i) => ({
          id: s.id,
          changed: s !== scenariosRef.current[i],
          paramsChanged: s.parameters !== scenariosRef.current[i]?.parameters
        }))
      });
      scenariosRef.current = scenarios;
    }
  }, [scenarios]);

  // Load from localStorage on mount (runs once)
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        // Clear old data - force fresh start for baseline system migration
        localStorage.removeItem(STORAGE_KEY);
        if (debugEnabled) {
          dlog('Cleared old scenario data for baseline system migration');
        }
      }
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }

    // Start with no scenarios - user will add them manually
    setScenarios([]);
    setActiveScenarioId(null);
  }, []); // No dependencies - runs once on mount

  // Update scenario parameters when API defaults become available (once)
  useEffect(() => {
    if (defaultParams && scenarios.length > 0 && !defaultsAppliedRef.current) {
      defaultsAppliedRef.current = true;
      setScenarios(currentScenarios =>
        currentScenarios.map(scenario => ({
          ...scenario,
          parameters: {
            ...defaultParams,
            analysis_settings: {
              ...defaultParams.analysis_settings,
              time_horizon_years: timeHorizonYears,
            },
          },
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultParams, timeHorizonYears]); // Intentionally not including scenarios.length to prevent loops

  // Save to localStorage whenever state changes
  useEffect(() => {
    if (scenarios.length > 0) {
      try {
        // Sanitize scenarios to prevent circular references and DOM elements
        const cleanScenarios = scenarios.map(scenario => sanitizeObject({
          id: scenario.id,
          title: scenario.title,
          scenarioType: scenario.scenarioType,
          link: scenario.link,
          parameters: scenario.parameters,
          analysisResult: scenario.analysisResult,
          isAnalyzing: scenario.isAnalyzing,
          error: scenario.error,
          visible: scenario.visible,
          isBaseline: scenario.isBaseline,
        }));

        const stateToStore: StoredState = {
          version: STORAGE_VERSION,
          scenarios: cleanScenarios,
          activeScenarioId,
          timeHorizonYears,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToStore));
      } catch (error) {
        console.warn('Failed to save scenarios to localStorage:', error);
        // Clear localStorage if it's corrupted
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (clearError) {
          console.warn('Failed to clear corrupted localStorage:', clearError);
        }
      }
    }
  }, [scenarios, activeScenarioId, timeHorizonYears]);

  // Get active scenario
  const activeScenario = scenarios.find(s => s.id === activeScenarioId);

  // Add new scenario
  const addScenario = useCallback((scenarioType: ScenarioType = 'buying', baseParams?: CalculatorParams) => {
    let newScenarioId: string | null = null;
    
    setScenarios(prev => {
      if (prev.length >= maxScenarios) {
        console.warn(`Maximum ${maxScenarios} scenarios allowed`);
        return prev;
      }

      // Always ensure we have valid parameters by falling back through the chain
      const currentActive = prev.find(s => s.id === activeScenarioId);
      const paramsToUse = baseParams || currentActive?.parameters || defaultParams || FALLBACK_DEFAULTS;

      // Count scenarios of the same type for naming
      const sameTypeCount = prev.filter(s => s.scenarioType === scenarioType).length;
      const title = scenarioType === 'buying'
        ? `Buying Scenario ${sameTypeCount + 1}`
        : `Renting Scenario ${sameTypeCount + 1}`;

      const newScenario: Scenario = {
        id: generateId(),
        title,
        scenarioType,
        link: undefined,
        parameters: paramsToUse,
        isAnalyzing: false,
        visible: true,
        isBaseline: prev.length === 0 && scenarioType === 'buying', // First scenario is baseline only if buying
      };

      // Store the new scenario ID to set as active after state update
      newScenarioId = newScenario.id;

      return [...prev, newScenario];
    });
    
    // Set as active scenario after state update
    if (newScenarioId) {
      setActiveScenarioId(newScenarioId);
    }
  }, [activeScenarioId, defaultParams, maxScenarios]);

  // Remove scenario
  const removeScenario = useCallback((scenarioId: string) => {
    setScenarios(prev => {
      const scenarioToRemove = prev.find(s => s.id === scenarioId);
      
      if (scenarioToRemove?.isBaseline && prev.length > 1) {
        // If removing baseline, set next scenario as baseline
        const filtered = prev.filter(s => s.id !== scenarioId);
        const newBaseline = filtered.find(s => s.scenarioType === 'buying') || filtered[0];
        
        const updated = filtered.map(s => ({
          ...s,
          isBaseline: newBaseline ? s.id === newBaseline.id && newBaseline.scenarioType === 'buying' : false
        }));
        
        // If we removed the active scenario, select the new baseline
        if (scenarioId === activeScenarioId) {
          setActiveScenarioId(newBaseline.id);
        }
        
        return updated;
      } else {
        const filtered = prev.filter(s => s.id !== scenarioId);

        // If we removed the active scenario, select the first remaining one
        if (scenarioId === activeScenarioId && filtered.length > 0) {
          setActiveScenarioId(filtered[0].id);
        }

        return filtered;
      }
    });
  }, [activeScenarioId]);

  // Update scenario
  const updateScenario = useCallback((scenarioId: string, updates: Partial<Scenario>) => {
    setScenarios(prev =>
      prev.map(scenario =>
        scenario.id === scenarioId
          ? { ...scenario, ...updates }
          : scenario
      )
    );
  }, []);

  // Update scenario parameters
  const updateScenarioParameters = useCallback((scenarioId: string, parameters: CalculatorParams) => {
    setScenarios(prev => {
      const existingScenario = prev.find(s => s.id === scenarioId);
      
      // Skip update if parameters haven't changed (same reference or deep equal)
      if (existingScenario?.parameters === parameters) {
        console.log('[UPDATE_PARAMS] SKIPPED - same reference');
        return prev; // Return same array reference
      }
      
      // Deep equality check to avoid unnecessary updates
      if (existingScenario && JSON.stringify(existingScenario.parameters) === JSON.stringify(parameters)) {
        console.log('[UPDATE_PARAMS] SKIPPED - same values');
        return prev; // Return same array reference
      }
      
      console.log('[UPDATE_PARAMS] UPDATING', {
        scenarioId,
        oldParams: existingScenario?.parameters,
        newParams: parameters,
      });
      
      if (debugEnabled) {
        const before = existingScenario?.parameters;
        const beforeRent = before?.renting_params;
        const afterRent = parameters?.renting_params;
        const beforeBuy = before?.buying_params;
        const afterBuy = parameters?.buying_params;
        dlog('[UpdateScenarioParameters]', {
          scenarioId,
          hadPrevious: !!before,
          afterKeys: Object.keys(parameters || {}),
          rentingBefore: beforeRent ? {
            current_rent_living_alone: beforeRent.current_rent_living_alone,
            current_rent_with_roommates: beforeRent.current_rent_with_roommates,
            move_out_year: beforeRent.move_out_year,
            rental_strategy: beforeRent.rental_strategy,
          } : undefined,
          rentingAfter: afterRent ? {
            current_rent_living_alone: afterRent.current_rent_living_alone,
            current_rent_with_roommates: afterRent.current_rent_with_roommates,
            move_out_year: afterRent.move_out_year,
            rental_strategy: afterRent.rental_strategy,
          } : undefined,
          buyingBefore: beforeBuy?.rental_strategy,
          buyingAfter: afterBuy?.rental_strategy,
        });
      }
      
      // Only create new array if parameters actually changed
      return prev.map(scenario =>
        scenario.id === scenarioId
          ? { ...scenario, parameters }
          : scenario
      );
    });
  }, []);

  // Update scenario title
  const updateScenarioTitle = useCallback((scenarioId: string, title: string) => {
    updateScenario(scenarioId, { title });
  }, [updateScenario]);

  const updateScenarioLink = useCallback((scenarioId: string, link?: string) => {
    updateScenario(scenarioId, { link });
  }, [updateScenario]);

  // Update scenario analysis result
  const updateScenarioResult = useCallback((scenarioId: string, result: any, error?: string) => {
    setScenarios(prev => {
      const existingScenario = prev.find(s => s.id === scenarioId);
      
      // Skip update if result hasn't changed
      if (existingScenario?.analysisResult === result && existingScenario?.error === error && existingScenario?.isAnalyzing === false) {
        return prev; // Return same array reference
      }
      
      return prev.map(scenario =>
        scenario.id === scenarioId
          ? {
              ...scenario,
              analysisResult: result,
              error,
              isAnalyzing: false
            }
          : scenario
      );
    });
  }, []);

  // Set scenario analyzing state
  const setScenarioAnalyzing = useCallback((scenarioId: string, isAnalyzing: boolean) => {
    setScenarios(prev => {
      const existingScenario = prev.find(s => s.id === scenarioId);
      
      // Skip update if analyzing state hasn't changed
      if (existingScenario?.isAnalyzing === isAnalyzing) {
        return prev; // Return same array reference
      }
      
      return prev.map(scenario =>
        scenario.id === scenarioId
          ? { ...scenario, isAnalyzing }
          : scenario
      );
    });
  }, []);

  // Toggle scenario visibility
  const toggleScenarioVisibility = useCallback((scenarioId: string) => {
    setScenarios(prev => {
      const existingScenario = prev.find(s => s.id === scenarioId);
      if (!existingScenario) return prev;
      
      const newVisibility = existingScenario.visible === false ? true : false;
      
      // Skip update if visibility hasn't changed
      if (existingScenario.visible === newVisibility) {
        return prev; // Return same array reference
      }
      
      return prev.map(scenario =>
        scenario.id === scenarioId
          ? { ...scenario, visible: newVisibility }
          : scenario
      );
    });
  }, []);

  // Toggle baseline - only one scenario can be baseline at a time
  const toggleBaseline = useCallback((scenarioId: string) => {
    setScenarios(prev => {
      const target = prev.find(s => s.id === scenarioId);
      if (!target || target.scenarioType !== 'buying') {
        return prev;
      }
      
      // Check if this scenario is already the baseline
      const currentBaseline = prev.find(s => s.isBaseline);
      if (currentBaseline?.id === scenarioId) {
        return prev; // Return same array reference
      }
      
      return prev.map(scenario => ({
        ...scenario,
        isBaseline: scenario.id === scenarioId
      }));
    });
  }, []);

  // Ensure a buying scenario is always the baseline when available
  useEffect(() => {
    setScenarios(prev => {
      if (prev.length === 0) return prev;

      const currentBaseline = prev.find(s => s.isBaseline);
      const firstBuying = prev.find(s => s.scenarioType === 'buying');

      // Baseline already valid
      if (currentBaseline && currentBaseline.scenarioType === 'buying') {
        return prev;
      }

      // No buying scenarios - clear any baseline flag
      if (!firstBuying) {
        if (!currentBaseline) return prev;
        let changed = false;
        const cleared = prev.map(s => {
          if (s.isBaseline) {
            changed = true;
            return { ...s, isBaseline: false };
          }
          return s;
        });
        return changed ? cleared : prev;
      }

      // Promote first buying scenario to baseline
      let changed = false;
      const updated = prev.map(s => {
        const shouldBeBaseline = s.id === firstBuying.id;
        if (s.isBaseline !== shouldBeBaseline) {
          changed = true;
          return { ...s, isBaseline: shouldBeBaseline };
        }
        return s;
      });
      return changed ? updated : prev;
    });
  }, [setScenarios, scenarios]);

  return {
    scenarios,
    activeScenario,
    activeScenarioId,
    timeHorizonYears,
    setActiveScenarioId,
    setTimeHorizonYears,
    addScenario,
    removeScenario,
    updateScenario,
    updateScenarioParameters,
    updateScenarioTitle,
    updateScenarioLink,
    updateScenarioResult,
    setScenarioAnalyzing,
    toggleScenarioVisibility,
    toggleBaseline,
    canAddScenario: scenarios.length < maxScenarios
  };
};
